/**
 * 오늘출발(배포 2b) 셀러 화면 공통 상수.
 *
 * 정본: hiver-dev-wiki `features/hiver/260904-오늘출발-PDP배송정보개편/`
 *   - `plan-배포2-3.md` §3-2(2b-S1~S8) · §3-9(2b-E1·E2) · §8(플래그·롤백)
 *   - `api-contract-배포2.md` §2(adminapi `domain/today_delivery/`)
 */

/**
 * 셀러어드민 오늘출발 UI 노출 모드 (플랜 §8 '셀러 메뉴 노출', PM 결정 2026-10-02 마스터 선공개).
 *
 * - `'OFF'`    — 아무에게도 보이지 않는다. 오늘출발 섹션·컬럼·필터·메뉴가 **하나도 렌더되지 않고 API 도 호출하지 않으며**,
 *                옵션 관리 경로도 등록하지 않는다(배포 1 화면과 동일).
 * - `'MASTER'` — 마스터 계정(셀러어드민 로그인 `localStorage.viewType === '1'`)에게만 `'ALL'` 처럼 보인다.
 *                일반 셀러에게는 `'OFF'` 와 같다 — 옵션 관리 경로는 등록되지만 주소를 직접 쳐도 NotFound 로 떨어진다.
 * - `'ALL'`    — 하이버 셀러 모두에게 보인다.
 *
 * 하이버 판정(`channelCode === 'hiver'`)과 AND 로 걸린다 — 화면은 이 값을 직접 보지 말고
 * `@/utils/todayDelivery` 의 `isTodayDeliverySeller`(채널 포함) · `isTodayDeliveryUiOpenToViewer`(채널 제외)로만 판정한다.
 *
 * 값 계획
 *  - 스테이징: `'ALL'`(S5 이후 지금 동작 그대로).
 *  - 상용 10/6 배포: `'MASTER'` — 마스터만 보고 10/8 내부 검증.
 *  - 상용 10/12 오픈 창: 이 값만 `'ALL'` 로 바꾸는 배포 — 셀러 오픈(2026-10-12 PM 결정, B1·A1·A3 정리 뒤).
 * 이 값은 셀러어드민 **화면 노출만** 정한다. 서버(adminapi) 오늘출발 API 는 막지 않는다(PM 2026-10-02).
 */
export type TodayDeliverySellerUiMode = 'OFF' | 'MASTER' | 'ALL'
export const TODAY_DELIVERY_SELLER_UI_MODE: TodayDeliverySellerUiMode = 'ALL'

/** 오늘출발 옵션 관리 화면 경로 — adminapi 메뉴 DML(`TB_C_MANAGER_MENU.C_MNGR_MENU_URL`)과 같은 문자열이어야 한다. */
export const TODAY_DELIVERY_OPTIONS_PATH = '/today-delivery/options'

/**
 * 오늘출발 셀러 가이드 — **게이트된 산출물**로 분리한 정적 파일(2b-S8).
 * 배포 1 의 배송 안내 가이드(`hiver-delivery-policy-guide.html`)는 손대지 않는다 —
 * 그 파일은 게이트와 무관하게 항상 서빙되므로 오늘출발 내용을 넣으면 OFF 에서도 노출된다.
 * 링크는 오늘출발 섹션 안에만 있으므로 게이트가 꺼져 있으면 어디에도 노출되지 않는다.
 */
export const TODAY_DELIVERY_GUIDE_URL = '/guide/hiver-today-delivery-guide.html'

/**
 * 오늘출발 신규 표시(NEW) 종료일 — 이 날(KST)까지 보이고 다음 날부터 자동으로 사라진다(PM 2026-10-12).
 * 대상: 사이드바 '오늘출발 옵션 관리'·'배송정책 관리' 항목과 그 그룹 제목('상품 관리'·'셀러 관리'), 배송정책 오늘출발 섹션 legend.
 * 쿠폰 NEW(`@/constants/couponPromo` 의 `COUPON_NEW_UNTIL`)와 같은 방식이다.
 */
export const TODAY_DELIVERY_NEW_UNTIL = '2026-11-30'

/** 오늘출발 NEW 를 보일지 — 브라우저 타임존과 무관하게 서비스 기준(Asia/Seoul) 날짜로 판정한다. */
export function isTodayDeliveryNewVisible(): boolean {
  const ymd = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Seoul' }) // 'YYYY-MM-DD'
  return ymd <= TODAY_DELIVERY_NEW_UNTIL
}

/** 주문 마감시각 — 09~23시 정시만 (adminapi `today_delivery/constants.py` CUTOFF_MIN/MAX_HOUR 와 같은 값). */
export const CUTOFF_MIN_HOUR = 9
export const CUTOFF_MAX_HOUR = 23
/**
 * 오늘출발 시작 설정 팝업의 주문 마감시각 초기값 — 정책 응답에 마감시각이 없을 때만 쓴다.
 * adminapi `today_delivery/constants.py` `DEFAULT_ORDER_CUTOFF_TIME`(14:00, 기본 정책 행·초기화 값)과 같은 값.
 */
export const TODAY_DELIVERY_DEFAULT_CUTOFF = '14:00'

/**
 * 오늘출발 옵션 관리(2b-S3)의 ②축 필터 · 목록 컬럼 값.
 * adminapi `GET …/today-delivery/options?apply_type=` 과 1:1 (플랜 §3-1 2b-A4 "국내/해외 · 전체/일부/미적용").
 */
export const APPLY_TYPE_ITEMS = [
  { title: '전체', value: 'ALL' },
  { title: '일부', value: 'PARTIAL' },
  { title: '미적용', value: 'NONE' },
]

/** 오늘출발 옵션 관리의 앱 노출 상태 필터 (`exposure_state`). */
export const EXPOSURE_STATE_ITEMS = [
  { title: '오늘출발', value: 'TODAY' },
  { title: '품절', value: 'SOLDOUT' },
  { title: '일반배송', value: 'NORMAL' },
]

/**
 * 상품 리스트(2b-S2)의 배송 구분 필터 — 어드민 검색 색인 `deliver_code` 와 같은 어휘.
 *
 * **세 값은 서로 배타다**: `01` 일반배송 / `02` 오늘출발(전 옵션) / `02p` 오늘출발(일부 옵션).
 * 하이버 ON 은 이 값을 adminapi 상품 목록(`GET /v1/sellers/{md_no}/products`)의 `deliverCode` 로 보낸다(260928 설계 9).
 *
 * 옵션 관리(2b-S3)의 `apply_type`(ALL/PARTIAL/NONE, 위 `APPLY_TYPE_ITEMS`)과는 **다른 축**이다 —
 * 그쪽은 adminapi 의 상품 단위 적용 구분이고, 이쪽은 검색 색인의 배송 구분이다.
 */
export const PRODUCT_DELIVERY_FILTER_ITEMS: { title: string; value: Product.DeliverCode }[] = [
  { title: '일반배송', value: '01' },
  { title: '오늘출발', value: '02' },
  { title: '오늘출발(일부)', value: '02p' },
]

/** 주문 목록 '배송구분' — 기존 국내/해외(order-api 무동작)에 오늘출발만 더한다 (2b-S5 · 2b-O2). */
export const ORDER_DELIVERY_TYPE_TODAY = { title: '오늘출발', value: 'TODAY_DELIVERY' }

/**
 * 배송구분 '오늘출발' 을 내놓을 주문 탭 (2b-O2).
 *
 * 이 값의 판정 원천은 출고 약속 스냅샷(`TB_C_ORDER_SHIP_PROMISE`)이고, order-api 가 그 EXISTS 절을
 * 붙여 둔 매퍼는 아래 세 탭뿐이다 — `/orders/all` · `/orders/product-prepare` · `/orders/delivery-prepare`.
 * 나머지 탭(배송중·배송완료·구매확정과 취소/환불 탭들)은 이 값을 상용 배송구분 코드(`DLVY_TYPE_CD`)와
 * 그대로 비교하므로 **무조건 0건**이 된다. 고를 수 있는데 늘 0건인 필터는 셀러에게 주문이 사라진 것처럼
 * 보이므로, 서버가 받는 탭에서만 선택지를 낸다.
 *
 * 빈 상태값은 목록이 `/orders/all` 로 가므로(OrderList `ORDER_LIST_ENDPOINT[status || '전체주문']`)
 * 여기 포함한다.
 */
export const TODAY_DELIVERY_ORDER_TABS = ['전체주문', '상품준비', '배송준비']

/** 주문 탭이 오늘출발 배송구분을 받는가 — 빈 상태값은 전체주문으로 본다. */
export function isTodayDeliveryOrderTab(status?: string | null): boolean {
  return TODAY_DELIVERY_ORDER_TABS.includes(status || '전체주문')
}

/** SKU 배송타입 (상품·옵션 `C_PRDUCT_DLVY_CD`). */
export const DELIVERY_CODE_ITEMS = [
  { title: '일반배송', value: '01' },
  { title: '오늘출발', value: '02' },
]

/**
 * 정책 동의 문안은 **서버가 단일 원천**이다 (adminapi `today_delivery/policy_agreement.json`,
 * 계약 §2 각주 '공통 JSON 벡터'). 화면은 정책 GET 응답의 `policy_agreement`(버전·원문 링크·항목)를
 * 그대로 그린다 — 사본을 두면 같은 버전에 다른 문안이 생긴다(R-SADM-6).
 *
 * 회귀 고정용 사본은 코드가 아니라 테스트 벡터에 있다: `scripts/today-delivery-agreement-vector.json`
 * (adminapi `policy_agreement.json` 과 바이트 동일 — 휴무 사유 벡터와 같은 방식).
 */

/** 엑셀 일괄 지정(2b-E1) 수용 기준 — 플랜 §3-9. */
export const BULK_EXCEL_MAX_BYTES = 2 * 1024 * 1024
export const BULK_EXCEL_MAX_ROWS = 5000
/**
 * 업로드 데이터 시트 이름 — 앞에서부터 찾고, 둘 다 없으면 활성 시트(셀러가 직접 만든 파일).
 * adminapi `today_delivery/excel._data_sheet`(새 양식 `오늘출발 지정` → 구 양식 `오늘출발`)와 같은 순서여야 한다.
 */
export const BULK_EXCEL_SHEET_TITLES = ['오늘출발 지정', '오늘출발']
/**
 * 새 양식의 회색 예시 행(2~4행) 표시 — A열이 문자열이고 앞뒤 공백을 뗀 값이 이 글자로 시작하면 데이터가 아니다.
 * 서버 파서(adminapi `today_delivery/excel`)가 같은 규칙으로 건너뛰고 5,000행 상한에도 세지 않는다.
 */
export const BULK_EXCEL_EXAMPLE_PREFIX = '예시'
/**
 * 배송코드 변경 한 번의 대상 SKU 합 상한 — adminapi `MAX_DELIVERY_CODE_SKUS`(6ad42b2). 넘으면 쓰기 전에 400 `TD_TOO_MANY_OPTIONS`.
 * 서버가 `error_data.max_options` 를 주면 그 값을, 없으면(엑셀 결과 행 사유 등) 이 값을 안내에 쓴다.
 */
export const MAX_DELIVERY_CODE_OPTIONS = 40000

/**
 * [오늘출발 상품 추가하기] 상품 검색 구분 — adminapi `GET …/today-delivery/products/search?search_type=` 과 1:1.
 * 글자는 상품 리스트 필터의 검색구분(상품명·상품번호·상품코드)과 같고, `상품관리코드` 는 상품 수정·옵션 편집기의 칸 이름 그대로다
 * (셀러가 옵션마다 적는 자체 관리코드 `custom_sku` — 그 코드를 가진 옵션이 하나라도 있는 상품, 정확일치. PM 2026-09-30, adminapi 666f162).
 */
export const PRODUCT_SEARCH_TYPE_ITEMS = [
  { title: '상품명', value: 'PRODUCT_NAME' },
  { title: '상품번호', value: 'PRODUCT_NO' },
  { title: '상품코드', value: 'PRODUCT_CODE' },
  { title: '상품관리코드', value: 'MANAGE_CODE' },
]
/** 상품번호 검색은 공백·쉼표·줄바꿈으로 여러 개 — adminapi `MAX_SEARCH_PRODUCT_NOS`(중복을 뺀 개수, 넘으면 400 `TD_TOO_MANY_PRODUCTS`) */
export const PRODUCT_SEARCH_MAX_PRODUCT_NOS = 50
/** 상품 검색 결과 한 페이지 줄 수 — 서버 `len` 은 1~50(기본 20) */
export const PRODUCT_SEARCH_PAGE_SIZE = 10
/**
 * 재고 소진 시 판매방식 변경(`PUT …/options/soldout-action`) 한 번에 실을 수 있는 옵션 수 — adminapi `MAX_SOLDOUT_ACTION_TARGETS`.
 * 목록 선택(한 페이지 최대 100줄)은 넘지 않고, [오늘출발 상품 추가하기] 는 옵션이 많은 상품에서 이 크기로 나눠 보낸다.
 */
export const SOLDOUT_ACTION_MAX_OPTIONS = 500
/** [오늘출발 상품 추가하기] 옵션 표 한 페이지 줄 수 — 옵션이 수만 건인 상품도 한 번에 그리지 않는다 */
export const ADD_OPTION_PAGE_SIZE = 50

/** 재고수량 입력 상한 — adminapi 옵션 재고 일괄수정(`inventory/batch`)이 0~30,000 만 받는다(옵션 편집기와 같은 값) */
export const STOCK_MAX = 30000

/**
 * 셀러 이행률 조회 기간(2b-E2) — 계약 `GET …/performance?days=30`.
 * 이번 배포는 30만 허용한다.
 */
export const TODAY_DELIVERY_PERFORMANCE_DAYS = 30
/**
 * 이행률을 내는 최소 표본 수 — 서버 규칙이다: 최근 30일 확정 표본이 이 값보다 적으면 `COLLECTING`(비율 없음),
 * 이상이면 `READY`(adminapi `today_delivery/schemas.TodayPerformanceResponse` "표본 30건 미만은 COLLECTING").
 * 화면은 판정하지 않고(서버 `status` 를 그대로 본다) **수집 중 안내 문구에만** 쓴다.
 */
export const TODAY_DELIVERY_PERFORMANCE_MIN_SAMPLE = 30
